import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import MuiTableRow from "@mui/material/TableRow";
import { useTableConstructor } from "layout/TableConstructor/context/tableConstructorContext";
import { IdentifiableWithCalculatedParams, ITableRowProps } from "layout/TableConstructor/types";
import { Dispatch, SetStateAction, useState } from "react";
import TableCell from "./TabelCell";
import CollapsingRow from "./TableCollapsingRow";
import RowCheckbox from "./TableRowCheckbox";

const CollapseButtonCell = ({ open, setOpen }: { open: boolean; setOpen: Dispatch<SetStateAction<boolean>> }) => (
  <TableCell onClick={(e) => e.stopPropagation()}>
    <IconButton
      sx={{ color: "base.200" }}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(!open);
      }}
      size='small'>
      {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
    </IconButton>
  </TableCell>
);

const Row = <T extends IdentifiableWithCalculatedParams, CollapsingRowName extends keyof T | undefined = undefined>({
  row,
  collapsingRowName,
  onRowClick,
  getRowBackgroundColor,
  getRowOpacity,
  selectedProps,
}: ITableRowProps<T, CollapsingRowName>) => {
  const { visibleColumnConfig } = useTableConstructor();

  const [open, setOpen] = useState(false);
  const rowBackgroundColor = getRowBackgroundColor?.(row);

  return (
    <>
      <MuiTableRow
        onClick={() => onRowClick?.(row)}
        sx={{
          backgroundColor: rowBackgroundColor || (row.isCollapsingRow ? "#fff" : undefined),
          cursor: onRowClick ? "pointer" : "default",
          opacity: getRowOpacity?.(row),
          "&:last-of-type": {
            "& .MuiTableCell-root": {
              "&:first-of-type": {
                borderLeftStyle: "solid",
                borderBottomLeftRadius: 8,
              },
              "&:last-of-type": {
                borderBottomRightRadius: 8,
              },
            },
          },
        }}
        key={`row-${row.id}`}>
        {row.isCollapsingRow && (
          <CollapseButtonCell
            open={open}
            setOpen={setOpen}
          />
        )}
        {!row.isCollapsingRow && (selectedProps || collapsingRowName) && (
          <TableCell
            onClick={(e) => selectedProps && e.stopPropagation()}
            sx={selectedProps && { cursor: "default" }}>
            {selectedProps && (
              <RowCheckbox
                selectedProps={selectedProps}
                row={row}
              />
            )}
          </TableCell>
        )}
        {visibleColumnConfig.map((col) => (
          <TableCell
            key={`cell-row-${row.id}-col-${col.name}`}
            sx={col.cellStyle}>
            {col.render(row)}
          </TableCell>
        ))}
      </MuiTableRow>
      <CollapsingRow
        open={open}
        row={row}
        onRowClick={onRowClick}
        collapsingRowName={collapsingRowName}
        getRowBackgroundColor={getRowBackgroundColor}
        selectedProps={selectedProps}
      />
    </>
  );
};

export default Row;
